import React, { useState, useEffect } from 'react';
import { useAgentAuthorization } from '../../../hooks/useAgentAuthorization';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Agent Create Modal
 *
 * Modal for creating new agent configurations.
 * IMPORTANT: Field names MUST match the agent.js config exactly.
 * These fields are directly mapped to environment variables in the agent.
 * 
 * For tenant_admin users, the tenant is auto-selected to their own tenant.
 */
const AgentCreateModal = ({ onSave, onClose, tenants = [] }) => {
  const { userRole, userTenant } = useAgentAuthorization();
  const { userData } = useAuth();
  
  // Determine if user is a tenant admin (can only create for their own tenant)
  const isTenantAdmin = userRole === 'tenant_admin';
  const isAdminOrSuper = userRole === 'admin' || userRole === 'superadmin';
  const isSiteScoped = ['site_admin', 'operator', 'viewer'].includes(userRole);
  
  // Phase 1C: Sites state
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  
  // Default state with values matching agent.js expectations
  // Note: agentId is intentionally left blank so the backend can auto-generate it
  // For tenant admins, auto-select their tenant
  // For site-scoped users, auto-assign siteId from user context
  const [formData, setFormData] = useState({
    agentId: '',
    agentName: '',
    agentTenant: isTenantAdmin ? userTenant : '',
    siteId: isSiteScoped ? (userData?.siteId || '') : '',
    agentSipId: '',
    agentRestId: '',
    heartbeatInterval: 30000,
    features: [],
  });
  
  // Phase 1C Part 2: Sites subscription
  useEffect(() => {
    const tenantId = userTenant || userData?.tenantId;
    if (!tenantId) {
      setSitesLoading(false);
      return;
    }
    
    const db = getFirestore();
    const sitesRef = collection(db, 'tenants', tenantId, 'sites');
    
    console.log('[AgentCreateModal] Setting up sites subscription for tenant:', tenantId);
    
    const unsubscribe = onSnapshot(
      sitesRef,
      (snapshot) => {
        const sitesArray = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.id,
        }));
        console.log(`[AgentCreateModal] Sites loaded: ${sitesArray.length} sites`, sitesArray);
        setSites(sitesArray);
        setSitesLoading(false);
      },
      (error) => {
        console.error('[AgentCreateModal] Error loading sites:', error);
        setSitesLoading(false);
      }
    );
    
    return () => unsubscribe();
  }, [userTenant, userData]);

  // Update tenant if userTenant changes (e.g., on initial load)
  useEffect(() => {
    if (isTenantAdmin && userTenant && !formData.agentTenant) {
      setFormData(prev => ({ ...prev, agentTenant: userTenant }));
    }
  }, [isTenantAdmin, userTenant]);
  
  // Update siteId if user context changes (for site-scoped users)
  useEffect(() => {
    if (isSiteScoped && userData?.siteId && !formData.siteId) {
      setFormData(prev => ({ ...prev, siteId: userData.siteId }));
    }
  }, [isSiteScoped, userData]);

  // Handle form input changes
  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle features multiselect
  const handleFeatureToggle = feature => {
    if (formData.features.includes(feature)) {
      setFormData({
        ...formData,
        features: formData.features.filter(f => f !== feature),
      });
    } else {
      setFormData({
        ...formData,
        features: [...formData.features, feature],
      });
    }
  };

  // Phase 1C Part 6: Handle form submission with validation
  const handleSubmit = e => {
    e.preventDefault();
    
    // Validate siteId is present
    if (!formData.siteId) {
      alert('Site is required. Please select a site before creating the agent.');
      return;
    }
    
    // Validate tenant is present
    if (!formData.agentTenant) {
      alert('Tenant is required. Please select a tenant before creating the agent.');
      return;
    }
    
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg p-6 w-full max-w-2xl"
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
      >
        <h2 className="text-xl font-bold mb-4">Create New Agent</h2>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Agent Name */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name *</label>
              <input
                type="text"
                name="agentName"
                value={formData.agentName}
                onChange={handleChange}
                placeholder="e.g., Office Monitor"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              <p className="mt-1 text-xs text-gray-500">Maps to AGENT_NAME in .env</p>
            </div>

            {/* Agent Tenant */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tenant *</label>
              {isTenantAdmin ? (
                // Tenant admins can only create agents for their own tenant
                <>
                  <input
                    type="text"
                    value={tenants.find(t => (t?.id || t?.tenantId) === userTenant)?.name || 
                           tenants.find(t => (t?.id || t?.tenantId) === userTenant)?.displayName || 
                           userTenant || 'Your Tenant'}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                  />
                  <p className="mt-1 text-xs text-gray-500">Agent will be created for your tenant</p>
                </>
              ) : (
                // Admins can select any tenant
                <>
                  <select
                    name="agentTenant"
                    value={formData.agentTenant}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select tenant...</option>
                    {tenants.map(tenant => {
                      const id = tenant?.id || tenant?.tenantId;
                      if (!id) return null;
                      const label = tenant?.name || tenant?.displayName || id;
                      return (
                        <option key={id} value={id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Maps to AGENT_TENANT in .env</p>
                </>
              )}
            </div>

            {/* Phase 1C Part 4: Site Selector - Role-based */}
            {isSiteScoped ? (
              // Site-scoped users: read-only, auto-assigned from user context
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Site</label>
                <input
                  type="text"
                  value={sites.find(s => s.id === formData.siteId)?.name || formData.siteId || 'Loading...'}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                />
                <p className="mt-1 text-xs text-gray-500">Auto-assigned from your user profile</p>
              </div>
            ) : (
              // Tenant admin and admin/superadmin: dropdown selector
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Site *</label>
                <select
                  name="siteId"
                  value={formData.siteId}
                  onChange={handleChange}
                  required
                  disabled={sitesLoading || sites.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-600"
                >
                  <option value="">
                    {sitesLoading ? 'Loading sites...' : sites.length === 0 ? 'No sites available - create a site first' : 'Select a site...'}
                  </option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
                {sites.length === 0 && !sitesLoading && (
                  <p className="mt-1 text-xs text-orange-600">
                    ⚠️ No sites found. Please create a site in the Sites page before creating agents.
                  </p>
                )}
                {sites.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">Required: Agent must be assigned to a site</p>
                )}
              </div>
            )}
          </div>

          <p className="mt-4 text-xs text-gray-500">
            Agent ID and advanced settings will be auto-assigned. You can view them later in the
            Agent details panel.
          </p>

          <div
            className="mt-6 flex justify-end space-x-3"
            style={{
              position: 'sticky',
              bottom: 0,
              background: '#fff',
              paddingTop: 16,
              paddingBottom: 8,
              borderTop: '1px solid #e5e7eb',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Create Agent
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgentCreateModal;
